/**
 * Build-time auth switches (`docs/HOSTING_PLAN.md` "Frontend"). `VITE_AUTH_ENABLED` turns the
 * Clerk gate on; everything else is unchanged when it is off. The publishable key lives in
 * `web/.env.local` (never committed; HANDOFF Q-7).
 */

export interface AuthEnv {
  VITE_AUTH_ENABLED?: string;
  VITE_CLERK_PUBLISHABLE_KEY?: string;
}

const ON = new Set(["1", "true", "yes", "on"]);

/** `VITE_AUTH_ENABLED=true` (or `1`); anything else, including unset, is off. */
export function authEnabled(env: AuthEnv = import.meta.env): boolean {
  return ON.has((env.VITE_AUTH_ENABLED ?? "").trim().toLowerCase());
}

/** The Clerk publishable key, or null when it is missing or blank. */
export function clerkPublishableKey(env: AuthEnv = import.meta.env): string | null {
  const key = (env.VITE_CLERK_PUBLISHABLE_KEY ?? "").trim();
  return key === "" ? null : key;
}

/** Client routes that render outside the gate (`docs/design/ia.md` §5). */
export const SIGN_IN_PATH = "/sign-in";
export const NO_ACCESS_PATH = "/no-access";

/** True for `/sign-in`, `/sign-in/...` (Clerk's sub-steps) and `/no-access`. */
export function isAuthPath(pathname: string): boolean {
  return (
    pathname === SIGN_IN_PATH ||
    pathname.startsWith(`${SIGN_IN_PATH}/`) ||
    pathname === NO_ACCESS_PATH
  );
}

/** The sign-in URL that brings the user back to `returnTo` (a same-origin path) afterwards. */
export function signInUrl(returnTo?: string | null): string {
  const target = safeReturnPath(returnTo);
  return target && target !== "/"
    ? `${SIGN_IN_PATH}?${new URLSearchParams({ redirect_url: target })}`
    : SIGN_IN_PATH;
}

/**
 * A `redirect_url` we accept: a same-origin path (`/runs/abc?tab=guide`). Anything with a
 * scheme or a protocol-relative `//host` is dropped, and the auth screens never redirect to
 * themselves.
 */
export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return null;
  }
  const pathname = value.split(/[?#]/, 1)[0] ?? "";
  return isAuthPath(pathname) ? null : value;
}
